import {
  DeleteOutlined,
  LoadingOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { ClubCrudApi } from '@hefe/store';
import {
  Button,
  Popconfirm,
  Popover,
  Space,
  Tooltip,
  Upload,
  message,
} from 'antd';
import { RcFile } from 'antd/lib/upload';
import { environment } from '../../../../../environments/environment';

interface ChildImageUploadCellProps {
  /** Current image path/URL */
  value?: string;
  /** Parent resource name (e.g. 'partners') */
  parentResource: string;
  /** Parent ID */
  parentId: number;
  /** Child relation name (e.g. 'sections') */
  relation: string;
  /** Child item ID */
  childId: number;
  /** Field/attribute name for the file (e.g. 'image_path') */
  fieldName: string;
}

/**
 * Child Image Upload Cell Component for Table
 *
 * Uses the child file upload endpoints:
 * - POST /crud/club/{resource}/{parentId}/{relation}/{childId}/upload/{attribute}
 * - DELETE /crud/club/{resource}/{parentId}/{relation}/{childId}/upload/{attribute}
 */
export default function ChildImageUploadCell({
  value,
  parentResource,
  parentId,
  relation,
  childId,
  fieldName,
}: ChildImageUploadCellProps) {
  const [uploadFile, { isLoading: isUploading }] =
    ClubCrudApi.useUploadChildFileMutation();
  const [deleteFile, { isLoading: isDeleting }] =
    ClubCrudApi.useDeleteChildFileMutation();

  const isLoading = isUploading || isDeleting;

  const beforeUpload = (file: RcFile) => {
    const isValidType =
      file.type === 'image/jpeg' ||
      file.type === 'image/png' ||
      file.type === 'image/webp';

    if (!isValidType) {
      message.error(t('errors.avatar_upload.wrong_format'));
      return false;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error(t('errors.avatar_upload.too_large'));
      return false;
    }

    handleUpload(file);
    return false;
  };

  const handleUpload = async (file: RcFile) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      await uploadFile({
        resource: parentResource,
        parentId,
        relation,
        childId,
        attribute: fieldName,
        file: formData,
      }).unwrap();

      message.success(t('success.upload'));
    } catch (error) {
      message.error(t('errors.upload_failed'));
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFile({
        resource: parentResource,
        parentId,
        relation,
        childId,
        attribute: fieldName,
      }).unwrap();

      message.success(t('success.deleted'));
    } catch (error) {
      message.error(t('errors.delete_failed'));
    }
  };

  // Construct full URL if value is a relative path
  const imageUrl = value?.startsWith('http')
    ? value
    : value
    ? `${environment.serverUrl}/storage/${value}`
    : null;

  if (isLoading) {
    return <LoadingOutlined style={{ fontSize: 20 }} />;
  }

  return (
    <Space size={8} onClick={(e) => e.stopPropagation()}>
      {imageUrl ? (
        <>
          <Popover
            trigger="click"
            content={
              <img
                src={imageUrl}
                alt=""
                style={{ maxHeight: 400, maxWidth: 800, objectFit: 'contain' }}
              />
            }
          >
            <img
              src={imageUrl}
              alt=""
              style={{ maxHeight: 40, maxWidth: 80, objectFit: 'contain' }}
            />
          </Popover>
          <Tooltip title={t('upload.image.edit')}>
            <Upload
              accept=".jpg,.jpeg,.png,.webp"
              showUploadList={false}
              beforeUpload={beforeUpload}
              disabled={isLoading}
            >
              <Button size="small" icon={<UploadOutlined />} />
            </Upload>
          </Tooltip>
          <Popconfirm
            title={t('questions.delete')}
            onConfirm={handleDelete}
            okText={t('yes')}
            cancelText={t('no')}
          >
            <Tooltip title={t('delete')}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </>
      ) : (
        <Upload
          accept=".jpg,.jpeg,.png,.webp"
          showUploadList={false}
          beforeUpload={beforeUpload}
          disabled={isLoading}
        >
          <Button size="small" icon={<UploadOutlined />}>
            {t('crud.image.upload')}
          </Button>
        </Upload>
      )}
    </Space>
  );
}
